const MEDIA_URL_PATTERN = /(?:https?|file):\/\/[^\s<>"'`\]\)]+/gi
const TRAILING_URL_PUNCTUATION_PATTERN = /[),.;:!?]+$/g
const IMAGE_EXTENSION_PATTERN = /\.(?:png|jpe?g|webp|gif|avif)$/i
const VIDEO_EXTENSION_PATTERN = /\.(?:mp4|webm|mov|m4v)$/i
const AUDIO_EXTENSION_PATTERN = /\.(?:mp3|wav|m4a|ogg|aac)$/i
const DATA_URL_MIME_PATTERN = /^data:([^;,]+)[^,]*,/i

export type GeneratedMediaPreviewKind = "image" | "video" | "audio"

export type GeneratedImagePreviewItem = {
  url: string
  filename: string
  kind: "image"
}

export type GeneratedMediaPreviewItem = {
  url: string
  filename: string
  kind: GeneratedMediaPreviewKind
}

function generatedMediaFilename(url: string, index: number, kind: GeneratedMediaPreviewKind) {
  if (url.startsWith("data:")) {
    if (kind === "video") return `generated-video-${index}.mp4`
    if (kind === "audio") return `generated-audio-${index}.mp3`
    return `generated-image-${index}.png`
  }
  const filename = url.split(/[?#]/)[0]?.split("/").at(-1)
  if (filename) return filename
  if (kind === "video") return `generated-video-${index}.mp4`
  if (kind === "audio") return `generated-audio-${index}.mp3`
  return `generated-image-${index}.png`
}

function generatedMediaKind(url: string): GeneratedMediaPreviewKind | undefined {
  const dataMime = url.match(DATA_URL_MIME_PATTERN)?.[1]?.toLowerCase()
  if (dataMime?.startsWith("image/")) return "image"
  if (dataMime?.startsWith("video/")) return "video"
  if (dataMime?.startsWith("audio/")) return "audio"
  const path = url.split(/[?#]/)[0]
  if (!/^(?:https?|file):\/\//i.test(url) || !path) return
  if (IMAGE_EXTENSION_PATTERN.test(path)) return "image"
  if (VIDEO_EXTENSION_PATTERN.test(path)) return "video"
  if (AUDIO_EXTENSION_PATTERN.test(path)) return "audio"
}

function generatedMediaKindFromMime(mime: string): GeneratedMediaPreviewKind | undefined {
  if (mime.startsWith("image/")) return "image"
  if (mime.startsWith("video/")) return "video"
  if (mime.startsWith("audio/")) return "audio"
}

export function cleanGeneratedMediaUrl(value: string) {
  const url = value.trim().replace(TRAILING_URL_PUNCTUATION_PATTERN, "")
  return generatedMediaKind(url) ? url : ""
}

export function generatedMediaPreviews(text: string) {
  const seen = new Set<string>()
  return [...text.matchAll(MEDIA_URL_PATTERN)].reduce<GeneratedMediaPreviewItem[]>((items, match) => {
    const url = cleanGeneratedMediaUrl(match[0])
    if (!url || seen.has(url)) return items
    const kind = generatedMediaKind(url)
    if (!kind) return items
    seen.add(url)
    return items.concat({ url, filename: generatedMediaFilename(url, items.length + 1, kind), kind })
  }, [])
}

export function generatedMediaPreviewsFromAttachments(
  attachments: Array<{ url?: string; mime?: string; filename?: string }>,
) {
  const seen = new Set<string>()
  return attachments.reduce<GeneratedMediaPreviewItem[]>((items, attachment) => {
    const url = attachment.url?.trim()
    if (!url || seen.has(url)) return items
    const kind = generatedMediaKindFromMime(attachment.mime ?? "") ?? generatedMediaKind(url)
    if (!kind) return items
    seen.add(url)
    return items.concat({
      url,
      filename: attachment.filename?.trim() || generatedMediaFilename(url, items.length + 1, kind),
      kind,
    })
  }, [])
}

export function cleanGeneratedImageUrl(value: string) {
  const url = cleanGeneratedMediaUrl(value)
  return generatedMediaKind(url) === "image" ? url : ""
}

export function generatedImagePreviews(text: string): GeneratedImagePreviewItem[] {
  return generatedMediaPreviews(text).filter((item): item is GeneratedImagePreviewItem => item.kind === "image")
}

function referenceValue(line: string) {
  return line
    .trim()
    .replace(/^(?:[-*+]\s+|\d+\.\s+)/, "")
    .replace(/^(?:\*\*)?(?:download|file|image|video|audio|preview)(?::\*\*|\*\*:|:)\s*/i, "")
    .trim()
}

function standaloneMediaReference(line: string) {
  const value = referenceValue(line)
  const markdownUrl = value.match(/^!?\[[^\]]*]\(\s*((?:https?|file):\/\/[^\s<>"'`]+)\s*(?:"[^"]*"|'[^']*')?\s*\)$/i)?.[1]
  const angleUrl = value.match(/^<([^>]+)>$/)?.[1]
  return !!cleanGeneratedMediaUrl(markdownUrl ?? angleUrl ?? value)
}

function standaloneImageReference(line: string) {
  const value = referenceValue(line)
  const markdownUrl = value.match(/^!?\[[^\]]*]\(\s*((?:https?|file):\/\/[^\s<>"'`]+)\s*(?:"[^"]*"|'[^']*')?\s*\)$/i)?.[1]
  const angleUrl = value.match(/^<([^>]+)>$/)?.[1]
  return !!cleanGeneratedImageUrl(markdownUrl ?? angleUrl ?? value)
}

export function stripStandaloneMediaReferences(text: string) {
  return text
    .split(/\r?\n/)
    .filter((line) => !standaloneMediaReference(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

export function stripStandaloneImageReferences(text: string) {
  return text
    .split(/\r?\n/)
    .filter((line) => !standaloneImageReference(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}
